import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AlertTriangle } from 'lucide-react'
import apiClient from '@/api/client'
import { ApiClientError } from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatDateHuman } from '@/lib/utils'
import { NumericKeypad } from './NumericKeypad'

interface ForceCloseDayDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dayId: string
  businessDate: string
}

const PIN_LENGTH = 4
const REASON_MIN = 4
const REASON_MAX = 500

/**
 * Admin escape hatch for prior-date days that genuinely cannot be counted —
 * vacation closures, drawer emptied by theft, setup-time rows. Force-closes
 * the day WITHOUT recording a cash count. The day's business_days row flips
 * to status='closed' with counted_cash / cash_variance NULL; sales numbers
 * are still snapshotted from the orders/payments ledger so reports show
 * real revenue. The required reason is persisted in closing_notes and the
 * day_close_audit_log so the owner can see exactly why this path was taken.
 *
 * Gated by manager/admin PIN — same gate as Reopen. The actual safety net
 * is the business_date < today guard inside the backend: today's day can
 * NEVER be force-closed, no matter what the operator types.
 */
export function ForceCloseDayDialog({
  open,
  onOpenChange,
  dayId,
  businessDate,
}: ForceCloseDayDialogProps) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setReason('')
      setPin('')
      setError('')
    }
  }, [open])

  const mut = useMutation({
    mutationFn: () =>
      apiClient.forceCloseBusinessDay(dayId, { pin, reason: reason.trim() }),
    onSuccess: () => {
      toastHelpers.success(`Day ${businessDate} force-closed without count`)
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['dailyClosings'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
      onOpenChange(false)
    },
    onError: (err) => {
      // The backend returns specific error codes so we can give the
      // operator a meaningful next step instead of a generic toast.
      if (err instanceof ApiClientError) {
        if (err.code === 'invalid_pin') {
          setError('Wrong PIN. Try again.')
          setPin('')
          return
        }
        if (err.code === 'reason_required') {
          setError('Reason must be 4–500 characters.')
          return
        }
        if (err.code === 'force_close_today_blocked') {
          setError(
            "Can't force-close today. Use the normal Finish the day flow instead.",
          )
          return
        }
      }
      setError('Something went wrong. Try again or contact support.')
    },
  })

  const trimmedReason = reason.trim()
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX
  const canSubmit = reasonValid && pin.length === PIN_LENGTH && !mut.isPending
  const masked = '•'.repeat(pin.length) + '·'.repeat(Math.max(0, PIN_LENGTH - pin.length))

  return (
    <Dialog open={open} onOpenChange={(o) => !mut.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl text-rose-700 dark:text-rose-300">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
            Force-close without count
          </DialogTitle>
          <DialogDescription className="text-base">
            This closes{' '}
            <span className="font-semibold text-foreground">
              {formatDateHuman(businessDate)}
            </span>{' '}
            without recording a cash count. Sales for this day stay in your
            reports, but the drawer reconciliation will be blank. Use this only
            when you genuinely cannot count.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="force-close-reason" className="text-base">
              Reason{' '}
              <span className="text-sm font-normal text-muted-foreground">
                (required — visible to the owner)
              </span>
            </Label>
            <Textarea
              id="force-close-reason"
              rows={3}
              maxLength={REASON_MAX}
              placeholder="e.g. Shop was closed for Eid holidays, no till activity"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (error) setError('')
              }}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {trimmedReason.length}/{REASON_MAX} — minimum {REASON_MIN} characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="force-close-pin" className="text-base">
              Manager or admin PIN
            </Label>
            <div
              className="flex h-16 items-center justify-center rounded-xl border-2 text-3xl font-bold tracking-[0.5em] tabular-nums select-none"
              aria-hidden="true"
            >
              {masked}
            </div>
            {/* Hidden focusable input so physical keyboards still work. */}
            <Input
              id="force-close-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={PIN_LENGTH}
              value={pin}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH)
                setPin(next)
                if (error) setError('')
              }}
              className="sr-only"
              aria-label="PIN"
            />
            {error && (
              <p className="text-sm font-medium text-rose-600 dark:text-rose-400">
                {error}
              </p>
            )}
          </div>

          <NumericKeypad
            value={pin}
            onChange={(next) => {
              setPin(next.slice(0, PIN_LENGTH))
              if (error) setError('')
            }}
            hideDecimal
            maxLength={PIN_LENGTH}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="lg"
            className="h-12 flex-1"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="h-12 flex-1 bg-rose-600 hover:bg-rose-700 text-white"
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
          >
            {mut.isPending ? 'Closing…' : 'Force-close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
